import { EditOutlined, MinusCircleOutlined, PlusCircleOutlined, PlusOutlined, StopOutlined, SwapOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  AutoComplete,
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { RefreshButton } from "@/components/RefreshButton";
import { useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { rupiahInput, rupiahSignedInput } from "../procurement/common";

type Account = Schemas["CashAccount"];
type Entry = Schemas["CashEntry"];
type EntryKind = Entry["kind"];
const DAY = "YYYY-MM-DD";
const ACCOUNT_KINDS: Account["kind"][] = ["bank", "cash", "ewallet", "marketplace"];
const ENTRY_KINDS: EntryKind[] = ["income", "expense", "adjustment", "transfer_in", "transfer_out", "supplier_payment", "supplier_refund", "receipt"];

const kindColor: Record<EntryKind, string> = {
  income: "success",
  receipt: "success",
  supplier_refund: "success",
  expense: "error",
  supplier_payment: "volcano",
  transfer_in: "blue",
  transfer_out: "blue",
  adjustment: "default",
};

/**
 * Kas & Bank (Plan §6.19, part C): the accounts money sits in and every
 * movement: by hand, transfers, supplier payments and (part D) marketplace
 * receipts. Nothing is deleted; a mistake is voided.
 */
export function Cash() {
  const { t, formatMoney, formatDate } = useI18n();
  const { can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const manage = can("finance.manage");
  const page = useListPage();
  const [account, setAccount] = useUrlFilter<string>("account", "");
  const [kind, setKind] = useUrlFilter<string>("kind", "");
  const [category, setCategory] = useUrlFilter<string>("category", "");
  const [q, setQ] = useUrlFilter<string>("q", "");
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [editing, setEditing] = useState<Account | "new" | null>(null);
  const [moving, setMoving] = useState<"income" | "expense" | "adjustment" | null>(null);
  const [transferring, setTransferring] = useState(false);
  const accounts = useQuery({ queryKey: ["finance", "cash", "accounts"], queryFn: () => unwrap(api.GET("/v1/finance/cash/accounts")) });
  const filters = {
    ...(account ? { accountId: account } : {}),
    ...(kind ? { kind: kind as EntryKind } : {}),
    ...(category ? { category } : {}),
    ...(q ? { q } : {}),
    ...(range ? { dateFrom: range[0].format(DAY), dateTo: range[1].format(DAY) } : {}),
  };
  const entries = useQuery({
    queryKey: ["finance", "cash", "entries", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/finance/cash/entries", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["finance", "cash"] });
  const voidEntry = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/v1/finance/cash/entries/{entryID}/void", { params: { path: { entryID: id } } })),
    onSuccess: () => (refresh(), void message.success(t("cash.voided"))),
    onError: (err) => void message.error(errorText(err)),
  });
  const items = accounts.data?.items ?? [];
  const open = items.filter((a) => a.active);
  const sum = accounts.data?.summary;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("cash.title")}
        </Typography.Title>
        {manage && (
          <Space wrap>
            <Button icon={<PlusCircleOutlined />} disabled={open.length === 0} onClick={() => setMoving("income")}>
              {t("cash.moneyIn")}
            </Button>
            <Button icon={<MinusCircleOutlined />} disabled={open.length === 0} onClick={() => setMoving("expense")}>
              {t("cash.moneyOut")}
            </Button>
            <Button icon={<SwapOutlined />} disabled={open.length < 2} onClick={() => setTransferring(true)}>
              {t("cash.transfer")}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing("new")}>
              {t("cash.addAccount")}
            </Button>
          </Space>
        )}
      </Flex>

      {sum && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Card size="small">
              <Statistic title={t("cash.totalBalance")} value={formatMoney(sum.balance)} />
            </Card>
          </Col>
          <Col xs={12} md={8}>
            <Card size="small">
              <Statistic title={t("cash.inThisMonth")} value={formatMoney(sum.moneyIn)} />
            </Card>
          </Col>
          <Col xs={12} md={8}>
            <Card size="small">
              <Statistic title={t("cash.outThisMonth")} value={formatMoney(sum.moneyOut)} />
            </Card>
          </Col>
        </Row>
      )}

      <Table<Account>
        rowKey="id"
        size="small"
        loading={accounts.isPending}
        dataSource={items}
        pagination={false}
        style={{ marginBottom: 24 }}
        locale={{ emptyText: t("cash.noAccounts") }}
        onRow={(a) => ({ onClick: () => setAccount(account === a.id ? "" : a.id), style: { cursor: "pointer" } })}
        rowClassName={(a) => (a.id === account ? "ant-table-row-selected" : "")}
        columns={[
          {
            title: t("cash.account"),
            render: (_, a) => (
              <Space orientation="vertical" size={0}>
                <Space>
                  <Typography.Text strong>{a.name}</Typography.Text>
                  <Tag>{t(`cash.kind.${a.kind}`)}</Tag>
                  {!a.active && <Tag color="default">{t("cash.closed")}</Tag>}
                </Space>
                {(a.bankName || a.accountNo) && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }} className="z-num">
                    {[a.bankName, a.accountNo].filter(Boolean).join(" · ")}
                  </Typography.Text>
                )}
              </Space>
            ),
          },
          { title: t("cash.in"), width: 150, align: "right", render: (_, a) => <span className="z-num">{formatMoney(a.moneyIn)}</span> },
          { title: t("cash.out"), width: 150, align: "right", render: (_, a) => <span className="z-num">{formatMoney(a.moneyOut)}</span> },
          {
            title: t("cash.balance"),
            width: 170,
            align: "right",
            render: (_, a) => (
              <Typography.Text strong className="z-num" type={a.balance < 0 ? "danger" : undefined}>
                {formatMoney(a.balance)}
              </Typography.Text>
            ),
          },
          {
            title: "",
            width: 50,
            render: (_, a) =>
              manage && (
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditing(a);
                  }}
                />
              ),
          },
        ]}
      />

      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <Select<string>
          value={account}
          onChange={setAccount}
          style={{ width: 220 }}
          options={[{ value: "", label: t("cash.allAccounts") }, ...items.map((a) => ({ value: a.id, label: a.name }))]}
        />
        <Select<string>
          value={kind}
          onChange={setKind}
          style={{ width: 200 }}
          options={[{ value: "", label: t("cash.allKinds") }, ...ENTRY_KINDS.map((k) => ({ value: k, label: t(`cash.entryKind.${k}`) }))]}
        />
        <Select<string>
          value={category}
          onChange={setCategory}
          style={{ width: 200 }}
          allowClear
          placeholder={t("cash.category")}
          options={(accounts.data?.categories ?? []).map((c) => ({ value: c, label: c }))}
        />
        <DatePicker.RangePicker value={range} onChange={(v) => setRange(v && v[0] && v[1] ? [v[0], v[1]] : null)} format="DD/MM/YYYY" />
        <Input.Search allowClear defaultValue={q} placeholder={t("cash.search")} onSearch={(v) => setQ(v.trim())} style={{ width: 260 }} />
        <div style={{ flex: 1 }} />
        <RefreshButton queryKeys={[["finance", "cash"]]} />
        <ExportButton
          path="/v1/finance/cash/entries/export"
          query={filters}
          filename={t("cash.title")}
          columns={[
            { key: "number", label: t("cash.number") },
            { key: "entryOn", label: t("cash.date"), kind: "date" },
            { key: "accountName", label: t("cash.account") },
            { key: "kind", label: t("cash.kindLabel"), values: Object.fromEntries(ENTRY_KINDS.map((k) => [k, t(`cash.entryKind.${k}`)])) },
            { key: "category", label: t("cash.category") },
            { key: "counterparty", label: t("cash.counterparty") },
            { key: "reference", label: t("bill.reference") },
            { key: "note", label: t("supplier.note") },
            { key: "amount", label: t("bill.amount"), kind: "number" },
            { key: "balanceAfter", label: t("cash.balance"), kind: "number" },
            { key: "status", label: t("common.status") },
            { key: "createdName", label: t("po.createdBy") },
          ]}
        />
      </Flex>
      <Table<Entry>
        rowKey="id"
        loading={entries.isFetching && !entries.data}
        dataSource={entries.data?.items ?? []}
        pagination={page.pagination(entries.data)}
        locale={{ emptyText: t("cash.noEntries") }}
        columns={[
          { title: t("cash.date"), dataIndex: "entryOn", width: 110, render: (v: string) => formatDate(v) },
          {
            title: t("cash.entry"),
            render: (_, e) => (
              <Space orientation="vertical" size={0}>
                <Space size={6} wrap>
                  <Tag color={kindColor[e.kind]}>{t(`cash.entryKind.${e.kind}`)}</Tag>
                  {e.category && <Typography.Text>{e.category}</Typography.Text>}
                  {e.counterparty && <Typography.Text type="secondary">· {e.counterparty}</Typography.Text>}
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  <span className="z-num">{e.number}</span>
                  {[e.reference, e.note].filter(Boolean).length > 0 && ` · ${[e.reference, e.note].filter(Boolean).join(" · ")}`}
                  {` · ${e.createdName}`}
                </Typography.Text>
              </Space>
            ),
          },
          ...(account ? [] : [{ title: t("cash.account"), dataIndex: "accountName", width: 180 }]),
          {
            title: t("bill.amount"),
            dataIndex: "amount",
            width: 160,
            align: "right",
            render: (n: number, e) => (
              <Typography.Text className="z-num" delete={e.status === "void"} type={e.status === "void" ? "secondary" : n < 0 ? "danger" : "success"}>
                {n > 0 ? "+" : ""}
                {formatMoney(n)}
              </Typography.Text>
            ),
          },
          ...(account
            ? [
                {
                  title: t("cash.balance"),
                  width: 160,
                  align: "right" as const,
                  render: (_: unknown, e: Entry) => (e.status === "void" ? "—" : <span className="z-num">{formatMoney(e.balanceAfter)}</span>),
                },
              ]
            : []),
          {
            title: "",
            width: 50,
            render: (_, e) =>
              manage &&
              e.status === "posted" &&
              !e.refId && (
                <Button
                  type="text"
                  danger
                  icon={<StopOutlined />}
                  title={t("cash.void")}
                  onClick={() =>
                    modal.confirm({
                      title: t("cash.void"),
                      content: t(e.transferId ? "cash.voidTransferText" : "cash.voidText", { number: e.number }),
                      okButtonProps: { danger: true },
                      okText: t("cash.void"),
                      cancelText: t("common.cancel"),
                      onOk: () => voidEntry.mutateAsync(e.id),
                    })
                  }
                />
              ),
          },
        ]}
      />

      <AccountModal account={editing} onClose={() => setEditing(null)} onDone={refresh} />
      <EntryModal
        kind={moving}
        accounts={open}
        categories={accounts.data?.categories ?? []}
        defaultAccount={account}
        onClose={() => setMoving(null)}
        onDone={refresh}
      />
      <TransferModal open={transferring} accounts={open} onClose={() => setTransferring(false)} onDone={refresh} />
    </>
  );
}

type AccountValues = { name: string; kind: Account["kind"]; bankName?: string; accountNo?: string; openingBalance?: number; openingOn: Dayjs; active?: boolean };

function AccountModal({ account, onClose, onDone }: { account: Account | "new" | null; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<AccountValues>();
  const kind = Form.useWatch("kind", form);
  const existing = account && account !== "new" ? account : null;
  const save = useMutation({
    mutationFn: (v: AccountValues) => {
      const body = {
        name: v.name.trim(),
        kind: v.kind,
        bankName: v.bankName?.trim() ?? "",
        accountNo: v.accountNo?.trim() ?? "",
        openingBalance: v.openingBalance ?? 0,
        openingOn: v.openingOn.format(DAY),
        active: v.active ?? true,
      };
      return existing
        ? unwrap(api.PUT("/v1/finance/cash/accounts/{accountID}", { params: { path: { accountID: existing.id } }, body }))
        : unwrap(api.POST("/v1/finance/cash/accounts", { body }));
    },
    onSuccess: () => {
      onDone();
      void message.success(t("cash.accountSaved"));
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={account !== null}
      title={t(existing ? "cash.editAccount" : "cash.addAccount")}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      destroyOnHidden
      afterOpenChange={(o) =>
        o &&
        form.setFieldsValue(
          existing
            ? { ...existing, openingOn: dayjs(existing.openingOn) }
            : { kind: "bank", openingBalance: 0, openingOn: dayjs().startOf("month"), active: true },
        )
      }
      afterClose={() => form.resetFields()}
    >
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => save.mutate(v)}>
        <Form.Item name="name" label={t("cash.accountName")} rules={[{ required: true, whitespace: true, max: 80 }]}>
          <Input placeholder={t("cash.accountNamePlaceholder")} />
        </Form.Item>
        <Form.Item name="kind" label={t("cash.kindLabel")}>
          <Select options={ACCOUNT_KINDS.map((k) => ({ value: k, label: t(`cash.kind.${k}`) }))} />
        </Form.Item>
        {kind !== "cash" && (
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="bankName" label={t(kind === "bank" ? "cash.bankName" : "cash.provider")}>
                <Input maxLength={80} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="accountNo" label={t("cash.accountNo")}>
                <Input maxLength={60} className="z-num" />
              </Form.Item>
            </Col>
          </Row>
        )}
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="openingBalance" label={t("cash.openingBalance")}>
              <InputNumber<number> precision={0} prefix="Rp" style={{ width: "100%" }} {...rupiahSignedInput} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="openingOn" label={t("cash.openingOn")} rules={[{ required: true }]} extra={t("cash.openingHelp")}>
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} allowClear={false} />
            </Form.Item>
          </Col>
        </Row>
        {existing && (
          <Form.Item name="active" label={t("cash.active")} valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

type EntryValues = { accountId: string; entryOn: Dayjs; amount: number; category?: string; counterparty?: string; reference?: string; note?: string };

function EntryModal({
  kind,
  accounts,
  categories,
  defaultAccount,
  onClose,
  onDone,
}: {
  kind: "income" | "expense" | "adjustment" | null;
  accounts: Account[];
  categories: string[];
  defaultAccount: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<EntryValues>();
  const save = useMutation({
    mutationFn: (v: EntryValues) =>
      unwrap(
        api.POST("/v1/finance/cash/entries", {
          body: {
            accountId: v.accountId,
            entryOn: v.entryOn.format(DAY),
            kind: kind!,
            amount: v.amount,
            category: v.category?.trim() ?? "",
            counterparty: v.counterparty?.trim() ?? "",
            reference: v.reference?.trim() ?? "",
            note: v.note?.trim() ?? "",
          },
        }),
      ),
    onSuccess: (x) => {
      onDone();
      void message.success(t("cash.entrySaved", { number: x.number }));
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={kind !== null}
      title={kind ? t(`cash.new.${kind}`) : ""}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      destroyOnHidden
      afterOpenChange={(o) =>
        o && form.setFieldsValue({ accountId: accounts.some((a) => a.id === defaultAccount) ? defaultAccount : accounts[0]?.id, entryOn: dayjs() })
      }
      afterClose={() => form.resetFields()}
    >
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => save.mutate(v)}>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="accountId" label={t("cash.account")} rules={[{ required: true }]}>
              <Select options={accounts.map((a) => ({ value: a.id, label: a.name }))} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="entryOn" label={t("cash.date")} rules={[{ required: true }]}>
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} allowClear={false} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="amount" label={t("bill.amount")} rules={[{ required: true }]} extra={kind === "adjustment" ? t("cash.adjustHelp") : undefined}>
          <InputNumber<number> min={kind === "adjustment" ? undefined : 1} precision={0} prefix="Rp" style={{ width: "100%" }} {...(kind === "adjustment" ? rupiahSignedInput : rupiahInput)} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="category" label={t("cash.category")}>
              <AutoComplete
                options={categories.map((c) => ({ value: c }))}
                showSearch={{ filterOption: (input, o) => String(o?.value ?? "").toLowerCase().includes(input.toLowerCase()) }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="counterparty" label={t(kind === "income" ? "cash.from" : "cash.to")}>
              <Input maxLength={120} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="reference" label={t("bill.reference")}>
          <Input maxLength={120} />
        </Form.Item>
        <Form.Item name="note" label={t("supplier.note")}>
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

type TransferValues = { fromAccountId: string; toAccountId: string; entryOn: Dayjs; amount: number; reference?: string; note?: string };

function TransferModal({ open, accounts, onClose, onDone }: { open: boolean; accounts: Account[]; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<TransferValues>();
  const save = useMutation({
    mutationFn: (v: TransferValues) =>
      unwrap(
        api.POST("/v1/finance/cash/transfers", {
          body: {
            fromAccountId: v.fromAccountId,
            toAccountId: v.toAccountId,
            entryOn: v.entryOn.format(DAY),
            amount: v.amount,
            reference: v.reference?.trim() ?? "",
            note: v.note?.trim() ?? "",
          },
        }),
      ),
    onSuccess: () => {
      onDone();
      void message.success(t("cash.transferSaved"));
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={open}
      title={t("cash.transfer")}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      destroyOnHidden
      afterOpenChange={(o) => o && form.setFieldsValue({ fromAccountId: accounts[0]?.id, toAccountId: accounts[1]?.id, entryOn: dayjs() })}
      afterClose={() => form.resetFields()}
    >
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => save.mutate(v)}>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="fromAccountId" label={t("cash.fromAccount")} rules={[{ required: true }]}>
              <Select options={accounts.map((a) => ({ value: a.id, label: a.name }))} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="toAccountId"
              label={t("cash.toAccount")}
              dependencies={["fromAccountId"]}
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator: (_, v) => (v && v === getFieldValue("fromAccountId") ? Promise.reject(new Error(t("cash.sameAccount"))) : Promise.resolve()),
                }),
              ]}
            >
              <Select options={accounts.map((a) => ({ value: a.id, label: a.name }))} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="amount" label={t("bill.amount")} rules={[{ required: true }]}>
              <InputNumber<number> min={1} precision={0} prefix="Rp" style={{ width: "100%" }} {...rupiahInput} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="entryOn" label={t("cash.date")} rules={[{ required: true }]}>
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} allowClear={false} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="reference" label={t("bill.reference")}>
          <Input maxLength={120} />
        </Form.Item>
        <Form.Item name="note" label={t("supplier.note")}>
          <Input maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
